import React from 'react';
import {
    Box,
    Button,
    Modal,
    SpaceBetween,
    StatusIndicator,
    Table,
} from '@cloudscape-design/components';
import { PublisherMetrics } from '../../api';

/**
 * Detail modal listing the full per-timeframe earner scores for an analyst.
 *
 * Rows are always rendered in the fixed timeframe order so the user can scan
 * the same table layout regardless of which fields the backfill has populated.
 * Sign colouring (success/error/N/A) and the confidence flag (Low confidence
 * vs OK vs blank) follow the spec exactly — see
 * docs/superpowers/specs/2026-05-27-publisher-metrics-chart-design.md.
 */
export interface PublisherMetricsModalProps {
    metrics?: PublisherMetrics | null;
    authorName?: string;
    visible: boolean;
    onDismiss: () => void;
}

interface Row {
    label: string;
    score: number | undefined | null;
    sampleCount: number | undefined | null;
}

function rowsForMetrics(m?: PublisherMetrics | null): Row[] {
    return [
        { label: '1h', score: m?.score1h, sampleCount: m?.sampleCount1h },
        { label: '1d', score: m?.score1d, sampleCount: m?.sampleCount1d },
        { label: '1w', score: m?.score1w, sampleCount: m?.sampleCount1w },
        { label: '2w', score: m?.score2w, sampleCount: m?.sampleCount2w },
        { label: '4w', score: m?.score4w, sampleCount: m?.sampleCount4w },
        { label: '2m', score: m?.score2m, sampleCount: m?.sampleCount2m },
        { label: '3m', score: m?.score3m, sampleCount: m?.sampleCount3m },
        { label: '4m', score: m?.score4m, sampleCount: m?.sampleCount4m },
        { label: '5m', score: m?.score5m, sampleCount: m?.sampleCount5m },
        { label: '6m', score: m?.score6m, sampleCount: m?.sampleCount6m },
    ];
}

const ScoreCell: React.FC<{ score: number | undefined | null }> = ({ score }) => {
    const hasScore = typeof score === 'number' && Number.isFinite(score);
    if (!hasScore) {
        return <Box color="text-status-inactive">N/A</Box>;
    }
    const s = score as number;
    const text = `${s >= 0 ? '+' : ''}${s.toFixed(1)}%`;
    return (
        <StatusIndicator type={s >= 0 ? 'success' : 'error'}>{text}</StatusIndicator>
    );
};

const QualityCell: React.FC<{ sampleCount: number | undefined | null }> = ({ sampleCount }) => {
    const n = typeof sampleCount === 'number' ? sampleCount : 0;
    if (n === 0) return <span data-testid="quality-blank" />;
    if (n < 2) {
        return <StatusIndicator type="warning">Low confidence</StatusIndicator>;
    }
    return <StatusIndicator type="success">OK</StatusIndicator>;
};

export const PublisherMetricsModal: React.FC<PublisherMetricsModalProps> = ({
    metrics,
    authorName,
    visible,
    onDismiss,
}) => {
    const rows = rowsForMetrics(metrics);
    const lastUpdated = metrics?.lastUpdated;

    return (
        <Modal
            visible={visible}
            onDismiss={onDismiss}
            size="medium"
            header={`${authorName ?? 'Analyst'} — earner scores`}
            footer={
                <SpaceBetween size="xs">
                    <Box color="text-status-inactive" fontSize="body-s">
                        Sample-weighted across all 10 timeframes. Stance==0 articles are excluded.
                    </Box>
                    {lastUpdated && (
                        <Box color="text-status-inactive" fontSize="body-s">
                            Last updated: {lastUpdated}
                        </Box>
                    )}
                    <Box float="right">
                        <Button variant="primary" onClick={onDismiss}>Close</Button>
                    </Box>
                </SpaceBetween>
            }
        >
            <Table<Row>
                items={rows}
                trackBy="label"
                variant="embedded"
                columnDefinitions={[
                    {
                        id: 'timeframe',
                        header: 'Timeframe',
                        cell: (row: Row) => (
                            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {row.label}
                            </span>
                        ),
                    },
                    {
                        id: 'score',
                        header: 'Stance-weighted return',
                        cell: (row: Row) => <ScoreCell score={row.score} />,
                    },
                    {
                        id: 'samples',
                        header: 'Samples (n)',
                        cell: (row: Row) => {
                            const n = typeof row.sampleCount === 'number' ? row.sampleCount : 0;
                            return <span style={{ fontFamily: 'monospace' }}>{n}</span>;
                        },
                    },
                    {
                        id: 'quality',
                        header: 'Quality',
                        cell: (row: Row) => <QualityCell sampleCount={row.sampleCount} />,
                    },
                ]}
            />
        </Modal>
    );
};
